import { useEffect, useReducer, useState } from "react";
import { useDispatch } from "react-redux";
import { createAction } from "@reduxjs/toolkit";
import type { Action } from "@reduxjs/toolkit";

import {
  Bill,
  Order,
  PurchaseOrderItem,
  ExtraItem,
  Breakdown,
  Cost,
  Tax,
} from "../../types/bills";

import { saveBill } from "../../redux/bills";

type DraftBy<T, K extends keyof T> = Omit<T, K> & {[key in K]: string};

export type DraftExtraItem = DraftBy<ExtraItem, "quantity" | "unitCost"> & { selected: boolean; };
export type DraftBreakdown = DraftBy<Breakdown, "quantity" | "unitCost">;
export type DraftCost = DraftBy<Cost, "quantity" | "unitCost">;
export type DraftPurchaseOrderItem = Omit<PurchaseOrderItem, "breakdowns" | "costs"> & {
  breakdowns: DraftBreakdown[];
  costs: DraftCost[];
  selected: boolean;
}
export type DraftItem = DraftExtraItem | DraftPurchaseOrderItem;
type DraftOrder = Omit<Order, "items"> & {
  items: DraftItem[];
}
export type DraftBill = Omit<Bill, "orders"> & {
  orders: DraftOrder[];
  exchangeRateUpdated: boolean;
};

function toDraft(bill: Bill): DraftBill {
  return {
    ...bill,
    exchangeRateUpdated: false,
    orders: bill.orders.map(
      o => ({
        ...o,
        items: o.items.map(
          i => {
            if ("breakdowns" in i) {
              return {
                ...i,
                breakdowns: i.breakdowns.map(
                  b => ({
                    ...b,
                    quantity: b.quantity.toFixed(0),
                    unitCost: b.unitCost.toFixed(4),
                  })
                ),
                costs: i.costs.map(
                  c => ({
                    ...c,
                    quantity: c.quantity.toFixed(0),
                    unitCost: c.unitCost.toFixed(4),
                  })
                ),
                selected: true,
              };
            } else {
              return {
                ...i,
                quantity: i.quantity.toFixed(0),
                unitCost: i.unitCost.toFixed(4),
                selected: true,
              };
            }
          }
        )
      })
    )
  };
}

function fromDraft(draft: DraftBill): Bill {
  return {
    ...draft,
    orders: draft.orders.map(
      o => ({
        ...o,
        items: o.items.filter(i => i.selected).map(
          i => {
            if ("breakdowns" in i) {
              return {
                ...i,
                breakdowns: i.breakdowns.map(
                  b => ({
                    ...b,
                    quantity: Number(b.quantity),
                    unitCost: Number(b.unitCost),
                  })
                ),
                costs: i.costs.map(
                  c => ({
                    ...c,
                    quantity: Number(c.quantity),
                    unitCost: Number(c.unitCost),
                  })
                ),
              };
            } else {
              return {
                ...i,
                quantity: Number(i.quantity),
                unitCost: Number(i.unitCost),
              };
            }
          }
        )
      })
    )
  };
}

function isValidTotal(value: string): boolean {
  const total = Number(value);
  return value !== "" && !Number.isNaN(total) && Number.isFinite(total);
}

function isValidQuantity(value: string): boolean {
  const quantity = Number(value);
  return value !== "" && Number.isInteger(quantity) && quantity >= 0;
}

function isValidDraft(draft: DraftBill): boolean {
  return (
    draft.number !== "" &&
    draft.orders.length > 0 &&
    draft.orders.every(
      o => (
        o.items.every(
          i => (
            !("breakdowns" in i) ?
              isValidQuantity(i.quantity) && isValidTotal(i.unitCost) :
              i.breakdowns.every(
                b => isValidQuantity(b.quantity) && isValidTotal(b.unitCost)
              ) && i.costs.every(
                c => isValidQuantity(c.quantity) && isValidTotal(c.unitCost)
              )
          )
        )
      )
    )
  );
}

interface Division {
  division_id: string;
  division_name: string;
  default_currency_id: string;
}

interface BillItemType {
  id?: string;
  name: string;
  description: string;
  cost: number;
  excludeFromMargin: boolean;
}

interface CodePayload {
  refId: string;
  billItemType: BillItemType;
}
interface DescriptionPayload {
  refId: string;
  description: string;
}
interface QuantityPayload {
  refId: string;
  quantity: string;
}
interface UnitCostPayload {
  refId: string;
  unitCost: string;
}
interface TaxesPayload {
  refId: string;
  taxes: Tax[];
}
interface AddExtraItemPayload {
  orderId: string;
  purchaseOrderId?: string;
}
interface ToggleItemSelectedPayload {
  refId: string;
}
interface ExchangeRateUpdatedPayload {
  updated: boolean;
}

const setNotes = createAction<string>("bill/set-notes");
const setBillNumber = createAction<string>("bill/set-bill-number");
const setBillDate = createAction<Date>("bill/set-bill-date");
const setDueDate = createAction<Date>("bill/set-due-date");
const setDivision = createAction<Division>("bill/set-division");
const setGlobalTax = createAction<Tax>("bill/set-global-tax");
const setCode = createAction<CodePayload>("bill/set-code");
const setDescription = createAction<DescriptionPayload>("bill/set-description");
const setQuantity = createAction<QuantityPayload>("bill/set-quantity");
const setUnitCost = createAction<UnitCostPayload>("bill/set-unit-cost");
const setTaxes = createAction<TaxesPayload>("bill/set-taxes");
const addExtraItem = createAction<AddExtraItemPayload>("bill/add-extra-item");
const toggleItemSelected = createAction<ToggleItemSelectedPayload>("bill/toggle-item-selected");

let newItemCounter = 0;
const updateExchangeRate = createAction<ExchangeRateUpdatedPayload>("bill/update-exchange-rate");
const resetDraft = createAction<Bill>("bill/reset");
const saveDraft = createAction<Bill>("bill/save");

function draftReducer(state: DraftBill, action: Action): DraftBill {
  if (setNotes.match(action)) {
    return { ...state, notes: action.payload };
  } else if (setBillNumber.match(action)) {
    return { ...state, number: action.payload };
  } else if (setBillDate.match(action)) {
    return { ...state, dateBillDate: action.payload };
  } else if (setDueDate.match(action)) {
    return { ...state, dateDue: action.payload };
  } else if (setDivision.match(action)) {
    return {
      ...state,
      currency: action.payload.default_currency_id,
      exchangeRateUpdated: action.payload.default_currency_id === state.currency && state.exchangeRateUpdated,
      division: {
        id: action.payload.division_id,
        name: action.payload.division_name,
      }
    };
  } else if (setGlobalTax.match(action)) {
    return {
      ...state,
      tax: action.payload,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(i => ({ ...i, taxes: [action.payload] }))
        })
      )
    };
  } else if (setCode.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(
            i => action.payload.refId === i.refId && !("breakdowns" in i) ? {
              ...i,
              code: action.payload.billItemType.name,
              billItemTypeId: action.payload.billItemType.id,
              description: action.payload.billItemType.description,
              unitCost: action.payload.billItemType.cost.toFixed(2),
              excludeFromMargin: action.payload.billItemType.excludeFromMargin,
            } : i
          )
        })
      )
    };
  } else if (setDescription.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(
            i => ("description" in i) && action.payload.refId === i.refId ? {
              ...i,
              description: action.payload.description
            } : i
          )
        })
      )
    };
  } else if (setQuantity.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(
            i => ("breakdowns" in i) ? {
              ...i,
              breakdowns: i.breakdowns.map(
                b => action.payload.refId === b.refId ? {
                  ...b,
                  quantity: action.payload.quantity.replaceAll(/[^0-9]/g, '')
                } : b
              ),
              costs: i.costs.map(
                c => action.payload.refId === c.refId ? {
                  ...c,
                  quantity: action.payload.quantity.replaceAll(/[^0-9]/g, '')
                } : c
              )
            } : (
              action.payload.refId === i.refId ? {
                ...i,
                quantity: action.payload.quantity.replaceAll(/[^0-9]/g, '')
              } : i
            )
          )
        })
      )
    };
  } else if (setUnitCost.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(
            i => ("breakdowns" in i) ? {
              ...i,
              breakdowns: i.breakdowns.map(
                b => action.payload.refId === b.refId ? {
                  ...b,
                  unitCost: action.payload.unitCost.replaceAll(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '')
                } : b
              ),
              costs: i.costs.map(
                c => action.payload.refId === c.refId ? {
                  ...c,
                  unitCost: action.payload.unitCost.replaceAll(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '')
                } : c
              )
            } : (
              action.payload.refId === i.refId ? {
                ...i,
                unitCost: action.payload.unitCost.replaceAll(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '')
              } : i
            )
          )
        })
      )
    };
  } else if (setTaxes.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => ({
          ...o,
          items: o.items.map(
            i => action.payload.refId === i.refId ? {
              ...i,
              taxes: action.payload.taxes
            } : i
          )
        })
      )
    };
  } else if (addExtraItem.match(action)) {
    return {
      ...state,
      orders: state.orders.map(
        o => action.payload.orderId === o.id ? {
          ...o,
          items: o.items.concat({
            refId: `new-${++newItemCounter}`,
            purchaseOrderId: action.payload.purchaseOrderId,
            code: "",
            description: "",
            quantity: "0",
            unitCost: "0.00",
            excludeFromMargin: false,
            taxes: [state.tax],
            selected: true,
          })
        } : o
      )
    };
  } else if (toggleItemSelected.match(action)) {
     return {
       ...state,
       orders: state.orders.map(
         o => ({
           ...o,
           items: o.items.map(
             i => ({
               ...i,
               selected: i.refId === action.payload.refId ? !i.selected : i.selected
             })
           )
         })
       )
     };
  } else if (updateExchangeRate.match(action)) {
    return {
      ...state,
      exchangeRateUpdated: action.payload.updated,
    };
  } else if (resetDraft.match(action)) {
    return toDraft(action.payload);
  } else if (saveDraft.match(action)) {
    const draft = toDraft(action.payload);
    state.orders.forEach(
      order => {
        const newOrder = draft.orders.find(o => o.id === order.id);
	if (!newOrder) {
          draft.orders = draft.orders.concat(order);
          return;
        }
	order.items.forEach(
          item => {
            if (!item.selected) {
              newOrder.items = newOrder.items.concat(item);
            }
          }
        );
      }
    );
    return draft;
  }
  return state;
}

export default function useDraft(bill: Bill) {
  const dispatch = useDispatch();
  const [draft, updateDraft] = useReducer(draftReducer, bill, toDraft);

  useEffect(() => {
    updateDraft(resetDraft(bill));
  }, [bill]);

  const isNew = !bill.id;
  const isEditable = true;
  const isValid = isValidDraft(draft);
  const updater = {
    setBillNumber: (billNumber: string) => updateDraft(setBillNumber(billNumber)),
    setNotes: (notes: string) => updateDraft(setNotes(notes)),
    setDivision: (division: Division) => updateDraft(setDivision(division)),
    setGlobalTax: (tax: Tax) => updateDraft(setGlobalTax(tax)),
    setBillDate: (billDate: Date) => updateDraft(setBillDate(billDate)),
    setDueDate: (dueDate: Date) => updateDraft(setDueDate(dueDate)),
    setCode: (refId: string, billItemType: BillItemType) => updateDraft(setCode({ refId, billItemType })),
    setDescription: (refId: string, description: string) => updateDraft(setDescription({ refId, description })),
    setQuantity: (refId: string, quantity: string) => updateDraft(setQuantity({ refId, quantity })),
    setUnitCost: (refId: string, unitCost: string) => updateDraft(setUnitCost({ refId, unitCost })),
    setTaxes: (refId: string, taxes: Tax[]) => updateDraft(setTaxes({ refId, taxes })),
    addExtraItem: (orderId: string, purchaseOrderId?: string) => updateDraft(addExtraItem({ orderId, purchaseOrderId })),
    toggleItemSelected: (refId: string) => updateDraft(toggleItemSelected({ refId })),
    updateExchangeRate: (updated: boolean) => updateDraft(updateExchangeRate({ updated })),
    resetDraft: (bill: Bill) => updateDraft(resetDraft(bill)),
  };
  const save = async () => {
    if (!isValid) {
      return;
    }
    const removedItems = draft.orders.reduce(
      (ri, o) => [
        ...ri,
        ...o.items.filter(i => !i.selected)
      ],
      []
    )
    const savedBill = await saveBill(fromDraft(draft), removedItems)(dispatch);
    updateDraft(saveDraft(savedBill));
    return savedBill;
  };

  return {
    draft,
    isNew,
    isEditable,
    isValid,
    updater,
    save,
  };
}

export type DraftUpdater = ReturnType<typeof useDraft>["updater"];
